import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Plus, UsersThree, PencilSimple, Trash, SlidersHorizontal, GearSix, CaretDown, UserPlus, Sparkle, FlowArrow,
} from "@phosphor-icons/react";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return "—"; }
}

const emptyForm = {
  name: "", company: "", email: "", phone: "", source: "",
  notes: "", follow_up_date: "", assigned_to_user_id: "",
  pipeline_id: "", stage_id: "", partner_id: "", referred_by_customer_id: "",
  next_action: "", opportunity_value: "",
};

const formatValue = (v) => (v === null || v === undefined || v === "" ? "—" : Number(v).toLocaleString());

export default function Leads() {
  const { currentOrg } = useAuth();
  const [pipelines, setPipelines] = useState([]);
  const [activePipelineId, setActivePipelineId] = useState("");
  const [leads, setLeads] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeStageId, setActiveStageId] = useState("__all__");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // lead_id or null
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [rowStageBusy, setRowStageBusy] = useState(null);
  const [convertingId, setConvertingId] = useState(null);
  const [scoringId, setScoringId] = useState(null);
  const [editingLead, setEditingLead] = useState(null);
  const [enrollTarget, setEnrollTarget] = useState(null);
  const [sequences, setSequences] = useState([]);
  const [selectedSeqId, setSelectedSeqId] = useState("");
  const [enrolling, setEnrolling] = useState(false);
  const [partners, setPartners] = useState([]);
  const [existingCustomers, setExistingCustomers] = useState([]);

  const customersEnabled = currentOrg?.enabled_features?.customers !== false;
  const nurtureEnabled = currentOrg?.enabled_features?.nurture_sequences !== false;
  const partnersEnabled = currentOrg?.enabled_features?.partners !== false;

  const openEnroll = async (lead) => {
    setEnrollTarget(lead);
    setSelectedSeqId("");
    try {
      const { data } = await api.get("/nurture-sequences");
      setSequences((data || []).filter((s) => s.is_active && (s.step_count || 0) > 0));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load sequences");
    }
  };

  const doEnroll = async () => {
    if (!enrollTarget || !selectedSeqId) return;
    setEnrolling(true);
    try {
      await api.post(`/leads/${enrollTarget.lead_id}/enroll`, { sequence_id: selectedSeqId });
      toast.success("Lead enrolled in sequence");
      setEnrollTarget(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Enroll failed");
    } finally { setEnrolling(false); }
  };

  const convertToCustomer = async (lead) => {
    setConvertingId(lead.lead_id);
    try {
      const { data } = await api.post(`/leads/${lead.lead_id}/convert-to-customer`);
      toast.success(`Created customer "${data.name}" from this lead`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not convert lead");
    } finally {
      setConvertingId(null);
    }
  };

  const scoreLead = async (lead) => {
    setScoringId(lead.lead_id);
    try {
      const { data } = await api.post(`/leads/${lead.lead_id}/score`);
      setLeads((all) => all.map((x) => x.lead_id === lead.lead_id ? data : x));
      toast.success(`Scored ${data.lead_score}/100 — ${data.score_reasoning?.slice(0, 80) || ""}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Score failed");
    } finally {
      setScoringId(null);
    }
  };

  const activePipeline = useMemo(
    () => pipelines.find((p) => p.pipeline_id === activePipelineId) || null,
    [pipelines, activePipelineId]
  );

  const stageCounts = useMemo(() => {
    const m = {};
    leads.forEach((l) => { m[l.stage_id] = (m[l.stage_id] || 0) + 1; });
    return m;
  }, [leads]);

  const filteredLeads = useMemo(() => {
    if (activeStageId === "__all__") return leads;
    return leads.filter((l) => l.stage_id === activeStageId);
  }, [leads, activeStageId]);

  const loadPipelines = useCallback(async () => {
    const { data } = await api.get("/pipelines");
    setPipelines(data);
    return data;
  }, []);

  const loadLeads = useCallback(async (pipelineId) => {
    if (!pipelineId) { setLeads([]); return; }
    const { data } = await api.get(`/pipelines/${pipelineId}/leads`);
    setLeads(data);
  }, []);

  const loadMembers = useCallback(async () => {
    try {
      const { data } = await api.get("/organizations/members");
      setMembers(data);
    } catch { /* non-blocking */ }
  }, []);

  const loadPartners = useCallback(async () => {
    try {
      const { data } = await api.get("/partners");
      setPartners((data || []).filter((p) => p.is_active));
    } catch { /* non-blocking — feature may be off */ }
  }, []);

  const loadExistingCustomers = useCallback(async () => {
    try {
      const { data } = await api.get("/customers");
      setExistingCustomers(data || []);
    } catch { /* non-blocking — customers feature may be off */ }
  }, []);

  const bootstrap = useCallback(async () => {
    setLoading(true);
    try {
      const [pl] = await Promise.all([loadPipelines(), loadMembers(), loadPartners(), loadExistingCustomers()]);
      const first = pl[0];
      const nextActive = pl.find((p) => p.pipeline_id === activePipelineId) ? activePipelineId : (first?.pipeline_id || "");
      setActivePipelineId(nextActive);
      if (nextActive) await loadLeads(nextActive);
    } catch {
      toast.error("Could not load leads");
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadPipelines, loadLeads, loadMembers, currentOrg?.org_id]);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  useEffect(() => {
    setActiveStageId("__all__");
    if (activePipelineId) loadLeads(activePipelineId);
  }, [activePipelineId, loadLeads]);

  const openCreate = () => {
    if (!activePipeline || activePipeline.stages.length === 0) {
      toast.error("This pipeline has no stages yet. Add stages first.");
      return;
    }
    setEditing(null);
    setForm({
      ...emptyForm,
      pipeline_id: activePipeline.pipeline_id,
      stage_id: activePipeline.stages[0].stage_id,
    });
    setDialogOpen(true);
  };

  const openEdit = (l) => {
    setEditing(l.lead_id);
    setEditingLead(l);
    setForm({
      name: l.name || "",
      company: l.company || "",
      email: l.email || "",
      phone: l.phone || "",
      source: l.source || "",
      notes: l.notes || "",
      follow_up_date: (l.follow_up_date || "").slice(0, 10),
      assigned_to_user_id: l.assigned_to_user_id || "",
      pipeline_id: l.pipeline_id,
      stage_id: l.stage_id,
      partner_id: l.partner_id || "",
      referred_by_customer_id: l.referred_by_customer_id || "",
      next_action: l.next_action || "",
      opportunity_value: l.opportunity_value ?? "",
    });
    setDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const name = form.name.trim();
    if (!name) { toast.error("Name is required"); return; }
    if (!form.pipeline_id || !form.stage_id) { toast.error("Pipeline and stage are required"); return; }
    setSaving(true);
    try {
      const body = {
        ...form,
        name,
        follow_up_date: form.follow_up_date || null,
        assigned_to_user_id: form.assigned_to_user_id || null,
        partner_id: form.partner_id || (editing ? "" : null),
        referred_by_customer_id: form.referred_by_customer_id || (editing ? "" : null),
        next_action: (form.next_action || "").trim(),
        opportunity_value: String(form.opportunity_value).trim() === "" ? null : Number(form.opportunity_value),
      };
      if (editing) {
        // Never send pipeline_id on edit — pipeline changes are out of scope
        const { pipeline_id: _pid, ...patch } = body;
        await api.patch(`/leads/${editing}`, patch);
        toast.success("Lead updated");
      } else {
        // On create, only send optional attribution ids if picked
        const createBody = { ...body };
        if (!form.partner_id) delete createBody.partner_id;
        if (!form.referred_by_customer_id) delete createBody.referred_by_customer_id;
        await api.post("/leads", createBody);
        toast.success("Lead added");
      }
      setDialogOpen(false);
      await loadLeads(activePipelineId);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    const lead = confirmDelete;
    setConfirmDelete(null);
    try {
      await api.delete(`/leads/${lead.lead_id}`);
      setLeads((all) => all.filter((x) => x.lead_id !== lead.lead_id));
      toast.success("Lead removed");
    } catch {
      toast.error("Could not delete lead");
    }
  };

  const handleStageChange = async (lead, newStageId) => {
    if (lead.stage_id === newStageId) return;
    setRowStageBusy(lead.lead_id);
    // Optimistic update
    const prevStageId = lead.stage_id;
    const stage = activePipeline?.stages.find((s) => s.stage_id === newStageId);
    setLeads((all) => all.map((x) =>
      x.lead_id === lead.lead_id ? { ...x, stage_id: newStageId, stage_name: stage?.name || x.stage_name } : x
    ));
    try {
      await api.patch(`/leads/${lead.lead_id}`, { stage_id: newStageId });
      toast.success(`Moved to ${stage?.name || "stage"}`);
    } catch {
      // Rollback
      setLeads((all) => all.map((x) =>
        x.lead_id === lead.lead_id ? { ...x, stage_id: prevStageId } : x
      ));
      toast.error("Could not update stage");
    } finally {
      setRowStageBusy(null);
    }
  };

  return (
    <div className="p-8 md:p-10 max-w-[1500px]" data-testid="leads-page">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-ink-secondary">
            {currentOrg?.name || "Workspace"}
          </div>
          <h1 className="mt-2 font-display text-4xl md:text-5xl font-light tracking-tight">Leads</h1>
          <p className="mt-3 text-sm text-ink-secondary max-w-lg">
            Track prospects through the pipelines you define. Every workspace starts with a Default Pipeline you can rename or replace.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Link
            to="/leads/pipelines"
            data-testid="leads-manage-pipelines"
            className="inline-flex items-center gap-2 rounded-full border border-white/10 px-4 py-2 text-sm text-ink-secondary hover:text-ink-primary hover:bg-white/5 transition-colors"
          >
            <GearSix weight="duotone" className="h-4 w-4" />
            Manage pipelines
          </Link>
          <button
            data-testid="leads-add-button"
            onClick={openCreate}
            disabled={!activePipeline || activePipeline.stages.length === 0}
            className="inline-flex items-center gap-2 rounded-full bg-white text-graphite-950 px-4 py-2 text-sm font-medium hover:bg-neutral-200 disabled:opacity-50 transition-colors"
          >
            <Plus weight="bold" className="h-4 w-4" />
            New lead
          </button>
        </div>
      </div>

      {/* Pipeline switcher + stage filter */}
      <div className="mt-8 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-[0.2em] text-ink-secondary">Pipeline</span>
          <Select value={activePipelineId} onValueChange={setActivePipelineId}>
            <SelectTrigger
              data-testid="leads-pipeline-switcher"
              className="w-[260px] bg-graphite-800 border-white/10 text-ink-primary"
            >
              <SelectValue placeholder="Choose pipeline" />
            </SelectTrigger>
            <SelectContent className="bg-graphite-800 border-white/10 text-ink-primary">
              {pipelines.map((p) => (
                <SelectItem key={p.pipeline_id} value={p.pipeline_id} data-testid={`leads-pipeline-option-${p.pipeline_id}`}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {activePipeline && activePipeline.stages.length > 0 && (
          <div className="flex flex-wrap items-center gap-2" data-testid="leads-stage-filter">
            <SlidersHorizontal weight="duotone" className="h-4 w-4 text-ink-secondary" />
            <button
              type="button"
              onClick={() => setActiveStageId("__all__")}
              data-testid="leads-stage-chip-__all__"
              className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs transition-colors ${
                activeStageId === "__all__"
                  ? "border-white/40 bg-white text-graphite-950"
                  : "border-white/10 bg-graphite-800 text-ink-secondary hover:text-ink-primary"
              }`}
            >
              All <span className="opacity-70">· {leads.length}</span>
            </button>
            {activePipeline.stages.map((s) => (
              <button
                key={s.stage_id}
                type="button"
                onClick={() => setActiveStageId(s.stage_id)}
                data-testid={`leads-stage-chip-${s.stage_id}`}
                className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs transition-colors ${
                  activeStageId === s.stage_id
                    ? "border-white/40 bg-white text-graphite-950"
                    : "border-white/10 bg-graphite-800 text-ink-secondary hover:text-ink-primary"
                }`}
              >
                {s.name} <span className="opacity-70">· {stageCounts[s.stage_id] || 0}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Table */}
      <div className="mt-6 rounded-md border border-white/10 bg-graphite-900 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-sm text-ink-secondary">Loading leads…</div>
        ) : !activePipeline ? (
          <div className="p-12 text-center text-sm text-ink-secondary">No pipeline selected.</div>
        ) : activePipeline.stages.length === 0 ? (
          <div className="p-12 text-center" data-testid="leads-no-stages">
            <div className="mx-auto h-12 w-12 rounded-md border border-dashed border-white/15 grid place-items-center">
              <SlidersHorizontal weight="duotone" className="h-5 w-5 text-ink-secondary" />
            </div>
            <div className="mt-4 font-display text-xl tracking-tight">Pipeline has no stages</div>
            <p className="mt-2 text-sm text-ink-secondary">Add at least one stage before creating leads.</p>
            <Link
              to="/leads/pipelines"
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-white text-graphite-950 px-4 py-2 text-sm font-medium hover:bg-neutral-200 transition-colors"
            >
              Manage pipelines
            </Link>
          </div>
        ) : filteredLeads.length === 0 ? (
          <div className="p-12 text-center" data-testid="leads-empty">
            <div className="mx-auto h-12 w-12 rounded-md border border-dashed border-white/15 grid place-items-center">
              <UsersThree weight="duotone" className="h-5 w-5 text-ink-secondary" />
            </div>
            <div className="mt-4 font-display text-xl tracking-tight">
              {leads.length === 0 ? "No leads yet" : "Nothing in this stage"}
            </div>
            <p className="mt-2 text-sm text-ink-secondary">
              {leads.length === 0 ? "Create your first lead to get started." : "Switch to another stage or clear the filter."}
            </p>
            {leads.length === 0 && (
              <button
                onClick={openCreate}
                data-testid="leads-empty-add"
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-white text-graphite-950 px-4 py-2 text-sm font-medium hover:bg-neutral-200 transition-colors"
              >
                <Plus weight="bold" className="h-4 w-4" />
                Add lead
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="leads-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-ink-secondary border-b border-white/10 bg-graphite-950/40">
                  <th className="px-5 py-3 font-medium">Name / Company</th>
                  <th className="px-5 py-3 font-medium">Stage</th>
                  <th className="px-5 py-3 font-medium">Score</th>
                  <th className="px-5 py-3 font-medium">Source</th>
                  <th className="px-5 py-3 font-medium">Assigned to</th>
                  <th className="px-5 py-3 font-medium">Follow-up</th>
                  <th className="px-5 py-3 font-medium">Next action</th>
                  <th className="px-5 py-3 font-medium text-right">Value</th>
                  <th className="px-5 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {filteredLeads.map((l) => (
                  <tr key={l.lead_id} className="hover:bg-white/[0.02]" data-testid={`leads-row-${l.lead_id}`}>
                    <td className="px-5 py-3">
                      <div className="text-ink-primary" data-testid={`leads-row-name-${l.lead_id}`}>{l.name}</div>
                      {(l.company || l.email) && (
                        <div className="text-[11px] text-ink-secondary">
                          {l.company}
                          {l.company && l.email ? " · " : ""}
                          {l.email}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <Select
                        value={l.stage_id}
                        onValueChange={(v) => handleStageChange(l, v)}
                        disabled={rowStageBusy === l.lead_id}
                      >
                        <SelectTrigger
                          data-testid={`leads-row-stage-${l.lead_id}`}
                          className="h-8 bg-graphite-800 border-white/10 text-ink-primary text-xs min-w-[140px]"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-graphite-800 border-white/10 text-ink-primary">
                          {activePipeline.stages.map((s) => (
                            <SelectItem key={s.stage_id} value={s.stage_id}>{s.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-5 py-3">
                      {(() => {
                        const s = l.lead_score;
                        if (s === null || s === undefined) {
                          return (
                            <span data-testid={`leads-score-none-${l.lead_id}`}
                              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border border-white/10 text-ink-secondary/70">
                              Not scored
                            </span>
                          );
                        }
                        const cls = s >= 70
                          ? "border-emerald-400/30 bg-emerald-400/5 text-emerald-200"
                          : s >= 40
                          ? "border-amber-400/30 bg-amber-400/5 text-amber-200"
                          : "border-red-400/30 bg-red-400/5 text-red-200";
                        return (
                          <span data-testid={`leads-score-${l.lead_id}`}
                            title={l.score_reasoning || undefined}
                            className={`inline-flex items-center gap-1 text-xs font-mono px-2 py-0.5 rounded-full border ${cls}`}>
                            {s}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-5 py-3 text-ink-secondary">{l.source || "—"}</td>
                    <td className="px-5 py-3 text-ink-secondary">{l.assigned_to?.name || "—"}</td>
                    <td className="px-5 py-3 text-ink-secondary">{formatDate(l.follow_up_date)}</td>
                    <td className="px-5 py-3 text-ink-secondary" data-testid={`leads-row-next-action-${l.lead_id}`}>{l.next_action || "—"}</td>
                    <td className="px-5 py-3 text-ink-secondary text-right tabular-nums" data-testid={`leads-row-value-${l.lead_id}`}>{formatValue(l.opportunity_value)}</td>
                    <td className="px-5 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          data-testid={`leads-row-score-${l.lead_id}`}
                          onClick={() => scoreLead(l)}
                          disabled={scoringId === l.lead_id}
                          className="p-2 rounded-md text-ink-secondary hover:text-amber-300 hover:bg-white/5 disabled:opacity-50"
                          title={l.lead_score !== null && l.lead_score !== undefined
                            ? "Regenerate lead score"
                            : "Generate AI lead score (0-100)"}
                        >
                          <Sparkle weight="bold" className="h-3.5 w-3.5" />
                        </button>
                        {customersEnabled && (
                          <button
                            data-testid={`leads-row-convert-${l.lead_id}`}
                            onClick={() => convertToCustomer(l)}
                            disabled={convertingId === l.lead_id}
                            className="p-2 rounded-md text-ink-secondary hover:text-emerald-300 hover:bg-white/5 disabled:opacity-50"
                            title="Convert to Customer (keeps this lead intact)"
                          >
                            <UserPlus weight="bold" className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {nurtureEnabled && (
                          <button
                            data-testid={`leads-row-enroll-${l.lead_id}`}
                            onClick={() => openEnroll(l)}
                            className="p-2 rounded-md text-ink-secondary hover:text-sky-300 hover:bg-white/5"
                            title="Enroll in nurture sequence"
                          >
                            <FlowArrow weight="bold" className="h-3.5 w-3.5" />
                          </button>
                        )}
                        <button
                          data-testid={`leads-row-edit-${l.lead_id}`}
                          onClick={() => openEdit(l)}
                          className="p-2 rounded-md text-ink-secondary hover:text-ink-primary hover:bg-white/5"
                          title="Edit"
                        >
                          <PencilSimple weight="bold" className="h-3.5 w-3.5" />
                        </button>
                        <button
                          data-testid={`leads-row-delete-${l.lead_id}`}
                          onClick={() => setConfirmDelete(l)}
                          className="p-2 rounded-md text-ink-secondary hover:text-accent-danger hover:bg-white/5"
                          title="Delete"
                        >
                          <Trash weight="bold" className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/edit lead dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          className="border-white/10 bg-graphite-800 text-ink-primary max-w-2xl"
          data-testid="lead-form-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-display font-light tracking-tight text-2xl">
              {editing ? "Edit lead" : "New lead"}
            </DialogTitle>
            <DialogDescription className="text-ink-secondary text-sm">
              {editing
                ? "Update this lead's details. Pipeline can't be changed once created."
                : "Track a new prospect through the selected pipeline."}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Name</label>
                <Input
                  data-testid="lead-form-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Alex Ford"
                  className="bg-graphite-900 border-white/10 focus-visible:ring-white/30 focus-visible:ring-1 focus-visible:border-white/30 text-ink-primary"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Company</label>
                <Input
                  data-testid="lead-form-company"
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  placeholder="Acme"
                  className="bg-graphite-900 border-white/10 focus-visible:ring-white/30 focus-visible:ring-1 focus-visible:border-white/30 text-ink-primary"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Email</label>
                <Input
                  data-testid="lead-form-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="bg-graphite-900 border-white/10 focus-visible:ring-white/30 focus-visible:ring-1 focus-visible:border-white/30 text-ink-primary"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Phone</label>
                <Input
                  data-testid="lead-form-phone"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="bg-graphite-900 border-white/10 focus-visible:ring-white/30 focus-visible:ring-1 focus-visible:border-white/30 text-ink-primary"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Source</label>
                <Input
                  data-testid="lead-form-source"
                  value={form.source}
                  onChange={(e) => setForm({ ...form, source: e.target.value })}
                  placeholder="Referral · Website · LinkedIn"
                  className="bg-graphite-900 border-white/10 focus-visible:ring-white/30 focus-visible:ring-1 focus-visible:border-white/30 text-ink-primary"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Follow-up date</label>
                <Input
                  data-testid="lead-form-followup"
                  type="date"
                  value={form.follow_up_date}
                  onChange={(e) => setForm({ ...form, follow_up_date: e.target.value })}
                  className="bg-graphite-900 border-white/10 focus-visible:ring-white/30 focus-visible:ring-1 focus-visible:border-white/30 text-ink-primary"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Next action</label>
                <Input
                  data-testid="lead-form-next-action"
                  value={form.next_action}
                  maxLength={500}
                  onChange={(e) => setForm({ ...form, next_action: e.target.value })}
                  placeholder="Follow up on proposal"
                  className="bg-graphite-900 border-white/10 focus-visible:ring-white/30 focus-visible:ring-1 focus-visible:border-white/30 text-ink-primary"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Opportunity value</label>
                <Input
                  data-testid="lead-form-opportunity-value"
                  type="number"
                  min="0"
                  step="any"
                  value={form.opportunity_value}
                  onChange={(e) => setForm({ ...form, opportunity_value: e.target.value })}
                  className="bg-graphite-900 border-white/10 focus-visible:ring-white/30 focus-visible:ring-1 focus-visible:border-white/30 text-ink-primary"
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Assigned to</label>
                <Select
                  value={form.assigned_to_user_id || "__none__"}
                  onValueChange={(v) => setForm({ ...form, assigned_to_user_id: v === "__none__" ? "" : v })}
                >
                  <SelectTrigger data-testid="lead-form-assignee" className="bg-graphite-900 border-white/10 text-ink-primary">
                    <SelectValue placeholder="Unassigned" />
                  </SelectTrigger>
                  <SelectContent className="bg-graphite-800 border-white/10 text-ink-primary">
                    <SelectItem value="__none__">Unassigned</SelectItem>
                    {members.map((m) => (
                      <SelectItem key={m.user_id} value={m.user_id}>{m.name || m.email}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Pipeline</label>
                <Select
                  value={form.pipeline_id}
                  onValueChange={(v) => {
                    const p = pipelines.find((x) => x.pipeline_id === v);
                    setForm({ ...form, pipeline_id: v, stage_id: p?.stages?.[0]?.stage_id || "" });
                  }}
                  disabled={!!editing}
                >
                  <SelectTrigger data-testid="lead-form-pipeline" className="bg-graphite-900 border-white/10 text-ink-primary disabled:opacity-70">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-graphite-800 border-white/10 text-ink-primary">
                    {pipelines.map((p) => (
                      <SelectItem key={p.pipeline_id} value={p.pipeline_id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Stage</label>
                <Select value={form.stage_id} onValueChange={(v) => setForm({ ...form, stage_id: v })}>
                  <SelectTrigger data-testid="lead-form-stage" className="bg-graphite-900 border-white/10 text-ink-primary">
                    <SelectValue placeholder="Choose a stage" />
                  </SelectTrigger>
                  <SelectContent className="bg-graphite-800 border-white/10 text-ink-primary">
                    {(pipelines.find((p) => p.pipeline_id === form.pipeline_id)?.stages || []).map((s) => (
                      <SelectItem key={s.stage_id} value={s.stage_id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {partnersEnabled && (
                <div className="space-y-1.5 sm:col-span-2">
                  <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Referring partner <span className="opacity-60">(optional)</span></label>
                  <select
                    data-testid="lead-form-partner"
                    value={form.partner_id}
                    onChange={(e) => setForm({ ...form, partner_id: e.target.value })}
                    className="w-full h-9 rounded-md bg-graphite-900 border border-white/10 px-3 text-sm text-ink-primary focus:outline-none focus:border-white/30"
                  >
                    <option value="">— None —</option>
                    {partners.map((p) => (
                      <option key={p.partner_id} value={p.partner_id}>{p.name}</option>
                    ))}
                  </select>
                  <p className="text-[11px] text-ink-secondary/60">Who referred this lead. Carried forward automatically when converting to a customer.</p>
                </div>
              )}
              {customersEnabled && (
                <div className="space-y-1.5 sm:col-span-2">
                  <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Referred by customer <span className="opacity-60">(optional)</span></label>
                  <select
                    data-testid="lead-form-referrer"
                    value={form.referred_by_customer_id}
                    onChange={(e) => setForm({ ...form, referred_by_customer_id: e.target.value })}
                    className="w-full h-9 rounded-md bg-graphite-900 border border-white/10 px-3 text-sm text-ink-primary focus:outline-none focus:border-white/30"
                  >
                    <option value="">— None —</option>
                    {existingCustomers.map((c) => (
                      <option key={c.customer_id} value={c.customer_id}>
                        {c.name}{c.company ? ` · ${c.company}` : ""}
                      </option>
                    ))}
                  </select>
                  <p className="text-[11px] text-ink-secondary/60">An existing customer who referred this lead. Carried forward on conversion.</p>
                </div>
              )}
              <div className="space-y-1.5 sm:col-span-2">
                <label className="text-xs uppercase tracking-[0.15em] text-ink-secondary">Notes</label>
                <Textarea
                  data-testid="lead-form-notes"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={3}
                  className="bg-graphite-900 border-white/10 focus-visible:ring-white/30 focus-visible:ring-1 focus-visible:border-white/30 text-ink-primary"
                />
              </div>
            </div>

            <DialogFooter>
              <button
                type="button"
                data-testid="lead-form-cancel"
                onClick={() => setDialogOpen(false)}
                className="rounded-md border border-white/10 px-4 py-2 text-sm hover:bg-white/5 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                data-testid="lead-form-submit"
                disabled={saving}
                className="rounded-md bg-white text-graphite-950 px-4 py-2 text-sm font-medium hover:bg-neutral-200 disabled:opacity-50 transition-colors"
              >
                {saving ? "Saving…" : editing ? "Save changes" : "Add lead"}
              </button>
            </DialogFooter>
          </form>

          {editing && editingLead && (editingLead.company_id || editingLead.grade || editingLead.icp_score !== null || (editingLead.drafted_emails || []).length > 0) && (
            <div className="mt-6 border-t border-white/10 pt-6 space-y-5" data-testid="lead-enrichment">
              <div className="text-[10px] uppercase tracking-[0.2em] text-ink-secondary">Inbound enrichment</div>
              {(editingLead.company_name || editingLead.company_id || editingLead.grade || editingLead.icp_score !== null) && (
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {editingLead.company_name && (
                    <div>
                      <div className="text-[11px] uppercase tracking-widest text-ink-secondary mb-1">Company</div>
                      <div data-testid="lead-enrichment-company" className="text-ink-primary">{editingLead.company_name}</div>
                    </div>
                  )}
                  {editingLead.grade && (
                    <div>
                      <div className="text-[11px] uppercase tracking-widest text-ink-secondary mb-1">Grade</div>
                      <span data-testid="lead-enrichment-grade" className="inline-block text-[11px] uppercase tracking-widest px-2 py-0.5 rounded-sm border border-emerald-400/30 text-emerald-300">
                        {editingLead.grade}
                      </span>
                    </div>
                  )}
                  {editingLead.icp_score !== null && editingLead.icp_score !== undefined && (
                    <div>
                      <div className="text-[11px] uppercase tracking-widest text-ink-secondary mb-1">ICP score</div>
                      <div data-testid="lead-enrichment-icp" className="text-ink-primary">{editingLead.icp_score}</div>
                    </div>
                  )}
                </div>
              )}
              {(editingLead.drafted_emails || []).length > 0 && (
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-ink-secondary mb-2">Drafted emails ({editingLead.drafted_emails.length})</div>
                  <div className="rounded-md border border-white/10 divide-y divide-white/5" data-testid="lead-enrichment-emails">
                    {editingLead.drafted_emails.map((d, i) => (
                      <div key={i} data-testid={`lead-enrichment-email-${i}`} className="px-4 py-3">
                        <div className="flex items-center justify-between gap-3">
                          <div className="text-sm text-ink-primary truncate">{d.subject || <span className="italic text-ink-secondary">(no subject)</span>}</div>
                          {d.sequence_position != null && (
                            <span className="text-[10px] uppercase tracking-widest text-ink-secondary shrink-0">#{d.sequence_position}</span>
                          )}
                        </div>
                        {d.body && <div className="mt-1.5 text-xs text-ink-secondary whitespace-pre-wrap line-clamp-4">{d.body}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent className="border-white/10 bg-graphite-800 text-ink-primary" data-testid="lead-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display font-light tracking-tight text-2xl">Delete lead?</AlertDialogTitle>
            <AlertDialogDescription className="text-ink-secondary text-sm">
              &ldquo;{confirmDelete?.name}&rdquo; will be removed from this workspace. This action can&rsquo;t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="lead-delete-cancel" className="border-white/10 bg-transparent hover:bg-white/5 text-ink-primary">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="lead-delete-confirm" onClick={handleDelete} className="bg-accent-danger text-graphite-950 hover:bg-red-300">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!enrollTarget} onOpenChange={(o) => !o && setEnrollTarget(null)}>
        <DialogContent className="border-white/10 bg-graphite-800 text-ink-primary max-w-md" data-testid="lead-enroll-dialog">
          <DialogHeader>
            <DialogTitle className="font-display font-light tracking-tight text-2xl">Enroll in sequence</DialogTitle>
            <DialogDescription className="text-ink-secondary text-sm">
              Pick an active sequence for &ldquo;{enrollTarget?.name}&rdquo;. First step starts today.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {sequences.length === 0 ? (
              <div className="text-sm text-ink-secondary" data-testid="lead-enroll-empty">
                No active sequences with steps. Create one first from Nurture Sequences.
              </div>
            ) : (
              <Select value={selectedSeqId} onValueChange={setSelectedSeqId}>
                <SelectTrigger data-testid="lead-enroll-select" className="bg-graphite-900 border-white/10">
                  <SelectValue placeholder="Pick a sequence" />
                </SelectTrigger>
                <SelectContent>
                  {sequences.map((s) => (
                    <SelectItem key={s.sequence_id} value={s.sequence_id}>
                      {s.name} ({s.step_count} step{s.step_count === 1 ? "" : "s"})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          <DialogFooter>
            <button type="button" onClick={() => setEnrollTarget(null)}
                    className="rounded-md border border-white/10 px-4 py-2 text-sm hover:bg-white/5">
              Cancel
            </button>
            <button type="button" data-testid="lead-enroll-confirm"
                    disabled={enrolling || !selectedSeqId}
                    onClick={doEnroll}
                    className="rounded-md bg-white text-graphite-950 px-4 py-2 text-sm font-medium hover:bg-neutral-200 disabled:opacity-50">
              {enrolling ? "Enrolling…" : "Enroll"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
